import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import { LegalDocumentKind } from '@provision/landing-page-utils';
import { useState } from 'react';
import { BlockContainer, BlockContent, BorderVerticalSeparator } from 'src/components';
import { type BundledActivationFlowConfig } from 'src/graphql';
import { useLegalDocumentLinks } from 'src/graphql/entities/legal-document/use-legal-document-links';
import { ConfigPreviewPane } from 'src/pages/Settings/memberships/components/config-preview-pane';
import { CopyConfigModal } from 'src/pages/Settings/memberships/components/copy-config-modal';
import { LandingPageConfigViewForm } from 'src/pages/Settings/memberships/components/landing-page-config-view-form';
import { LegalDocumentPanel } from 'src/pages/Settings/memberships/components/legal-document/legal-document-panel';
import { MembershipsConfigPanelContent } from 'src/pages/Settings/memberships/components/memberships-config-panel-content';
import { MembershipsConfigPanelHeader } from 'src/pages/Settings/memberships/components/memberships-config-panel-header';
import { type PlansTabFormValues } from 'src/pages/Settings/memberships/components/plans-tab/types';
import { PricingExportTemplate } from 'src/pages/Settings/memberships/components/pricing-export-template/pricing-export-template';
import { useBeforeUnloadWarning } from 'src/pages/Settings/memberships/hooks/use-before-unload-warning';
import { useClientSegments } from 'src/pages/Settings/memberships/hooks/use-client-segments';
import { useConfigPanelResize } from 'src/pages/Settings/memberships/hooks/use-config-panel-resize';
import { useCopyConfig } from 'src/pages/Settings/memberships/hooks/use-copy-config';
import { useLandingPageSurface } from 'src/pages/Settings/memberships/hooks/use-landing-page-surface';
import { useLegalDocumentSurface } from 'src/pages/Settings/memberships/hooks/use-legal-document-surface';
import { useMembershipsConfigPreview } from 'src/pages/Settings/memberships/hooks/use-memberships-config-preview';
import { useMembershipsConfigSurface } from 'src/pages/Settings/memberships/hooks/use-memberships-config-surface';
import { useMembershipsPermissions } from 'src/pages/Settings/memberships/hooks/use-memberships-permissions';
import { usePricingExport } from 'src/pages/Settings/memberships/hooks/use-pricing-export';
import {
  ConfigPanel,
  MembershipsConfigContainer,
  PanelContent,
  PanelFooter,
} from 'src/pages/Settings/memberships/styled';
import {
  membershipsBlockContentProps,
  membershipsLayoutSx,
  saveButtonSx,
} from 'src/pages/Settings/memberships/styles';
import type { ConfigSurface } from 'src/pages/Settings/memberships/types';

const SURFACE_TITLES: Record<ConfigSurface, string> = {
  memberships: 'Configuration',
  landingPage: 'Landing Page',
  termsOfService: 'Terms of Service',
  privacyPolicy: 'Privacy Policy',
};

export type MembershipsConfigViewLayoutProps = {
  selectedSegmentId: string | null;
  onSegmentChange: (segmentId: string | null) => void | Promise<void>;
  onCreateConfig: () => void;
  isNewConfig: boolean;
  isLoading: boolean;
  error: unknown;
  data: PlansTabFormValues | null;
  activationFlowConfig: BundledActivationFlowConfig | null;
  formKey: string;
  partnerId: number | null;
  activationFlowConfigId: string | null;
  onSaved?: () => void | Promise<void>;
  showCreateConfig: boolean;
  isCreatingConfig: boolean;
  refreshErrorMessage: string | null;
  onRetryRefresh: () => void;
  /** True while a save's refetches are in flight; the form must not re-seed from partial data. */
  isApplyingSave?: boolean;
};

export const MembershipsConfigViewLayout = ({
  selectedSegmentId,
  onSegmentChange,
  onCreateConfig,
  isNewConfig,
  isLoading,
  error,
  data,
  activationFlowConfig,
  formKey,
  partnerId,
  activationFlowConfigId,
  onSaved,
  showCreateConfig,
  isCreatingConfig,
  refreshErrorMessage,
  onRetryRefresh,
  isApplyingSave = false,
}: MembershipsConfigViewLayoutProps) => {
  const {
    loading: permissionsLoading,
    canEditMembershipsConfig,
    canManageLandingPages,
  } = useMembershipsPermissions();

  const [selectedSurface, setSelectedSurface] = useState<ConfigSurface>('memberships');
  // Every page but Membership Options rides on the landing page permission.
  const activeSurface = canManageLandingPages ? selectedSurface : 'memberships';
  const isMembershipsSurface = activeSurface === 'memberships';
  const isLandingPageSurface = activeSurface === 'landingPage';

  const membershipsSurface = useMembershipsConfigSurface({
    partnerId,
    selectedSegmentId,
    activationFlowConfigId,
    formKey,
    canEdit: canEditMembershipsConfig,
    onSaved,
  });
  const landingPageSurface = useLandingPageSurface({
    partnerId,
    selectedSegmentId,
    canManage: canManageLandingPages,
  });
  const legalDocumentLinks = useLegalDocumentLinks({ partnerId, brandId: selectedSegmentId });
  const termsOfServiceSurface = useLegalDocumentSurface({
    kind: LegalDocumentKind.TermsOfService,
    partnerId,
    selectedSegmentId,
    canManage: canManageLandingPages,
  });
  const privacyPolicySurface = useLegalDocumentSurface({
    kind: LegalDocumentKind.PrivacyPolicy,
    partnerId,
    selectedSegmentId,
    canManage: canManageLandingPages,
  });
  const surfaces = {
    memberships: membershipsSurface,
    landingPage: landingPageSurface,
    termsOfService: termsOfServiceSurface,
    privacyPolicy: privacyPolicySurface,
  };
  const activeSurfaceController = surfaces[activeSurface];
  const activeLegalDocumentSurface =
    activeSurface === 'termsOfService' || activeSurface === 'privacyPolicy'
      ? surfaces[activeSurface]
      : null;

  // Every surface stays mounted, so any can be dirty regardless of which is on screen.
  const hasUnsavedChangesOnAnySurface = Object.values(surfaces).some(
    (surface) => surface.hasUnsavedChanges,
  );
  useBeforeUnloadWarning(hasUnsavedChangesOnAnySurface);

  const {
    segments,
    segmentsLoading,
    hasPartner,
    hasSegments,
    handleSegmentChange,
    isInitialSegmentSelectionPending,
  } = useClientSegments({
    partnerId,
    selectedSegmentId,
    onSegmentChange,
    hasUnsavedChanges: hasUnsavedChangesOnAnySurface,
  });

  const { containerRef, separatorProps, isDragging, configPanelWidth } =
    useConfigPanelResize();
  const preview = useMembershipsConfigPreview({
    activationFlowConfig,
    data,
    liveFormValues: membershipsSurface.liveFormValues,
  });
  const {
    exportGrid,
    exportTemplateData,
    handleExportClick,
    handleTemplateReady,
    isExporting,
    templateRef,
    templateStyle,
  } = usePricingExport({ activationFlowConfig, segments, selectedSegmentId });
  const { isModalOpen, closeModal, handleCopyConfig, isCopying, configMenuOptions } =
    useCopyConfig({ partnerId, onCopied: onSaved });

  const isPanelLoading =
    !hasPartner || segmentsLoading || isLoading || isInitialSegmentSelectionPending;
  const showConfigMenu =
    isMembershipsSurface && !permissionsLoading && canEditMembershipsConfig;
  const showSaveButton =
    activeSurfaceController.hasUnsavedChanges || activeSurfaceController.isSaving;
  const selectedSegment = segments.find(
    (candidate) => String(candidate.brandId) === selectedSegmentId,
  );

  return (
    <BlockContainer sx={membershipsLayoutSx}>
      <BlockContent {...membershipsBlockContentProps}>
        <MembershipsConfigContainer
          ref={containerRef}
          sx={{ userSelect: isDragging ? 'none' : 'auto' }}
        >
          <ConfigPreviewPane
            isLandingPageSurface={isLandingPageSurface}
            preview={preview}
            landingPageSurface={landingPageSurface}
            legalDocumentSurface={activeLegalDocumentSurface}
            segment={selectedSegment}
            termsOfServiceUrl={legalDocumentLinks.termsOfServiceUrl || undefined}
          />

          <BorderVerticalSeparator {...separatorProps} />

          <ConfigPanel customWidth={configPanelWidth}>
            <MembershipsConfigPanelHeader
              showSurfaceSelect={canManageLandingPages}
              activeSurface={activeSurface}
              onSurfaceChange={setSelectedSurface}
              title={SURFACE_TITLES[activeSurface]}
              segments={segments}
              segmentsLoading={!hasPartner || segmentsLoading}
              selectedSegmentId={selectedSegmentId}
              onSegmentChange={handleSegmentChange}
              showExportButton={isMembershipsSurface}
              exportDisabled={
                !selectedSegmentId || !activationFlowConfig || isPanelLoading || isExporting
              }
              isExporting={isExporting}
              onExportClick={handleExportClick}
              showMenu={showConfigMenu}
              menuOptions={configMenuOptions}
              showReadOnlyWarning={!permissionsLoading && !canEditMembershipsConfig}
              refreshErrorMessage={refreshErrorMessage}
              onRetryRefresh={onRetryRefresh}
            />

            <PanelContent>
              <Box hidden={!isMembershipsSurface}>
                <MembershipsConfigPanelContent
                  error={error}
                  isLoading={isPanelLoading}
                  hasSegments={hasSegments}
                  selectedSegmentId={selectedSegmentId}
                  data={data}
                  isNewConfig={isNewConfig}
                  showCreateConfig={showCreateConfig}
                  onCreateConfig={onCreateConfig}
                  isCreatingConfig={isCreatingConfig}
                  readOnly={membershipsSurface.readOnly}
                  formKey={formKey}
                  isApplyingSave={isApplyingSave}
                  saveSlots={membershipsSurface.saveSlots}
                  onFormValuesChange={membershipsSurface.onFormValuesChange}
                />
              </Box>
              {canManageLandingPages ? (
                <Box hidden={!isLandingPageSurface}>
                  <LandingPageConfigViewForm
                    key={landingPageSurface.formKey}
                    data={landingPageSurface.data}
                    readOnly={!canManageLandingPages}
                    segmentLogoUrl={selectedSegment?.logo}
                    onFormValuesChange={landingPageSurface.onFormValuesChange}
                    {...landingPageSurface.saveSlots}
                  />
                </Box>
              ) : null}
              {canManageLandingPages && selectedSegmentId !== null
                ? (['termsOfService', 'privacyPolicy'] as const).map((surface) => (
                    <Box key={surface} hidden={activeSurface !== surface}>
                      <LegalDocumentPanel
                        surface={surfaces[surface]}
                        partnerId={partnerId}
                        selectedSegmentId={selectedSegmentId}
                        segments={segments}
                      />
                    </Box>
                  ))
                : null}
            </PanelContent>

            {isExporting && exportGrid ? (
              <PricingExportTemplate
                ref={templateRef}
                data={exportTemplateData}
                templateStyle={templateStyle}
                onTemplateReady={handleTemplateReady}
              />
            ) : null}

            {showSaveButton ? (
              <PanelFooter>
                <LoadingButton
                  variant="contained"
                  fullWidth
                  onClick={activeSurfaceController.handleSave}
                  sx={saveButtonSx}
                  disabled={activeSurfaceController.isSaveDisabled}
                  loading={activeSurfaceController.isSaving}
                >
                  Save Configuration
                </LoadingButton>
              </PanelFooter>
            ) : null}
          </ConfigPanel>
        </MembershipsConfigContainer>

        <CopyConfigModal
          open={isModalOpen}
          onClose={closeModal}
          onSubmit={handleCopyConfig}
          segments={segments}
          loading={isCopying}
        />
      </BlockContent>
    </BlockContainer>
  );
};
